/**
 * Purpose: Provide a Google raster base-layer source/layer for supported lyrs codes.
 * Assumptions: Caller chooses a single layer code (or none) and maplibre renders tiled raster imagery.
 * External refs: react-map-gl/maplibre Source/Layer and Google Maps tile lyrs mapping gist.
 */
import { Layer, Source, type LayerProps } from "react-map-gl/maplibre";

const GOOGLE_SUBDOMAINS = ["mt0", "mt1", "mt2", "mt3"];

const GOOGLE_MAPS_LAYER_OPTIONS = {
  m: { id: "roadmap", label: "Standard roadmap" },
  r: { id: "alt-roadmap", label: "Alternative roadmap" },
  h: { id: "roads-only", label: "Roads only" },
  p: { id: "terrain", label: "Terrain" },
  t: { id: "terrain-only", label: "Terrain only" },
  s: { id: "satellite-only", label: "Satellite only" },
  y: { id: "hybrid", label: "Hybrid satellite" },
} as const;

const GOOGLE_RASTER_LAYER: LayerProps = {
  id: "google-base-raster",
  type: "raster",
  paint: {
    "raster-opacity": 0.9,
  },
};

type GoogleMapsLayerCode = keyof typeof GOOGLE_MAPS_LAYER_OPTIONS;

type GMapsBaseLayerProps = {
  idPrefix: string;
  layer: GoogleMapsLayerCode | "none";
  beforeId?: string;
};

export function GMapsBaseLayer({
  idPrefix,
  layer,
  beforeId,
}: GMapsBaseLayerProps) {
  if (layer === "none") {
    return null;
  }

  const tiles = GOOGLE_SUBDOMAINS.map(
    (subdomain) =>
      `https://${subdomain}.google.com/vt/lyrs=${layer}&x={x}&y={y}&z={z}`,
  );

  return (
    <Source
      id={`${idPrefix}-source`}
      type="raster"
      tiles={tiles}
      tileSize={256}
      attribution="© Google"
    >
      <Layer
        {...GOOGLE_RASTER_LAYER}
        id={`${idPrefix}-${GOOGLE_MAPS_LAYER_OPTIONS[layer].id}`}
        beforeId={beforeId}
      />
    </Source>
  );
}
